import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { plata, cant, hoy } from '../utils/formato';

// La primera mirada del día, pensada para verse desde el teléfono.
//
// Arriba va lo de HOY, que es lo que él quiere saber cuando abre el programa
// estando fuera del local: cuánto se ha vendido y cuánto de eso entró en
// efectivo. El resto del mes va debajo.
function Panel({ irA }) {
  const [hoyDatos, setHoyDatos] = useState(null);
  const [mes, setMes] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [cartera, setCartera] = useState({ clientes: [], total: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    const d = hoy();
    Promise.all([
      axios.get(`/api/ganancias?desde=${d}&hasta=${d}`).then(r => vivo && setHoyDatos(r.data)),
      axios.get('/api/ganancias').then(r => vivo && setMes(r.data)),
      axios.get('/api/inventario').then(r => vivo &&
        setAlertas((r.data || []).filter(p => p.alerta !== 'bien'))),
      axios.get('/api/clientes/cartera').then(r => vivo &&
        setCartera(r.data || { clientes: [], total: 0 })),
    ]).catch(() => vivo && setError('No se pudo cargar el panel'));
    return () => { vivo = false; };
  }, []);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!hoyDatos || !mes) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const h = hoyDatos.resumen, hc = hoyDatos.caja;
  const m = mes.resumen, mc = mes.caja;
  // La mano de obra de las instalaciones se resta igual que el costo de la
  // mercancía. Tiene que dar lo MISMO que la pantalla de Ganancias: dos cifras
  // distintas para lo mismo hacen dudar de las dos.
  const gananciaHoy = h.total - h.costo - (h.costo_instalacion || 0);
  const gananciaMes = m.total - m.costo - (m.costo_instalacion || 0);
  const gastosMes = (mes.gastos?.v || 0) + (mes.nomina?.v || 0);
  const quedaMes = gananciaMes - gastosMes;

  // A quién hay que llamar hoy. Un saldo total no dice a quién.
  const atrasados = cartera.clientes.filter(c => (c.dias || 0) >= 30);

  const Dato = ({ rotulo, valor, pie, color }) => (
    <div className="dato" style={{ '--color': color }}>
      <div className="rotulo">{rotulo}</div>
      <div className="valor cifra">{valor}</div>
      {pie && <div className="pie">{pie}</div>}
    </div>
  );

  return (
    <>
      <div className="encabezado">
        <h1>Panel</h1>
        <p>Lo de hoy, y cómo va el mes</p>
      </div>

      {atrasados.length > 0 && (
        <div className="aviso malo">
          <strong>
            {atrasados.length === 1
              ? `${atrasados[0].nombre} lleva ${atrasados[0].dias} días debiendo ${plata(atrasados[0].saldo)}`
              : `${atrasados.length} clientes llevan más de 30 días sin pagar`}
          </strong>
          {atrasados.length > 1 && (
            <> — {atrasados.slice(0, 3).map(c => `${c.nombre} (${c.dias} d)`).join(', ')}
              {atrasados.length > 3 && ` y ${atrasados.length - 3} más`}.</>
          )}
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => irA('clientes')}>
            A quién cobrar
          </button>
        </div>
      )}

      {alertas.length > 0 && (
        <div className="aviso ojo">
          <strong>
            {alertas.length === 1 ? 'Hay 1 producto por reponer'
                                  : `Hay ${alertas.length} productos por reponer`}
          </strong>
          {' — '}
          {alertas.slice(0, 3).map(p => p.nombre).join(', ')}
          {alertas.length > 3 && ` y ${alertas.length - 3} más`}.
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => irA('inventario')}>
            Ver cuáles
          </button>
        </div>
      )}

      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em',
                    textTransform: 'uppercase', color: 'var(--tinta-tenue)',
                    margin: '0 0 10px 2px' }}>
        Hoy
      </div>
      <div className="rejilla">
        <Dato rotulo="🛒 Vendido" valor={plata(h.total)}
              pie={`${h.ventas} ${h.ventas === 1 ? 'venta' : 'ventas'}`}
              color="var(--venta)" />
        <Dato rotulo="💵 En efectivo" valor={plata(hc.efectivo)}
              pie="lo que debería haber en la caja" color="var(--bueno)" />
        <Dato rotulo="🏦 Por transferencia" valor={plata(hc.transferencia)}
              pie="lo que debería estar en el banco" color="var(--acero)" />
        <Dato rotulo="💰 Dejó" valor={plata(gananciaHoy)}
              pie={h.total > 0 ? `${(gananciaHoy / h.total * 100).toFixed(0)}% de lo vendido` : '—'}
              color="var(--acento)" />
      </div>

      {hc.credito > 0 && (
        <div className="aviso" style={{ marginTop: -10 }}>
          Además se fiaron <strong>{plata(hc.credito)}</strong> hoy. Esa plata no está
          en la caja ni en el banco todavía.
        </div>
      )}

      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em',
                    textTransform: 'uppercase', color: 'var(--tinta-tenue)',
                    margin: '26px 0 10px 2px' }}>
        Este mes
      </div>
      <div className="rejilla">
        <Dato rotulo="🛒 Vendido" valor={plata(m.total)}
              pie={`${m.ventas} ${m.ventas === 1 ? 'venta' : 'ventas'}`}
              color="var(--venta)" />
        <Dato rotulo="🔩 Materiales" valor={plata(m.materiales)}
              pie={`costaron ${plata(m.costo)}`} color="var(--acero)" />
        <Dato rotulo="🔧 Instalaciones" valor={plata(m.instalacion)}
              pie={m.costo_instalacion > 0
                ? `${plata(m.costo_instalacion)} de mano de obra`
                : 'sin mano de obra anotada'}
              color="var(--obra)" />
        <Dato rotulo="💰 Ganancia" valor={plata(gananciaMes)}
              pie={m.total > 0 ? `${(gananciaMes / m.total * 100).toFixed(0)}% de lo vendido` : '—'}
              color="var(--acento)" />
      </div>

      <div className="rejilla">
        {gastosMes > 0 && (
          <Dato rotulo="💸 Gastos y nómina" valor={plata(gastosMes)}
                pie={mes.nomina?.v > 0
                  ? `${plata(mes.gastos?.v || 0)} gastos · ${plata(mes.nomina.v)} nómina`
                  : 'del mes'}
                color="var(--malo)" />
        )}
        {gastosMes > 0 && (
          <Dato rotulo={quedaMes >= 0 ? '✅ Queda' : '⚠️ Falta'}
                valor={plata(Math.abs(quedaMes))}
                pie="después de todo" color={quedaMes >= 0 ? 'var(--bueno)' : 'var(--malo)'} />
        )}
        {cartera.total > 0 && (
          <Dato rotulo="📝 Le deben" valor={plata(cartera.total)}
                pie={`${cartera.clientes.length} ${cartera.clientes.length === 1 ? 'cliente' : 'clientes'}`}
                color="var(--ojo)" />
        )}
      </div>

      <div className="bloque">
        <h2>⚡ Qué quieres hacer</h2>
        <div className="cuerpo">
          <div style={{ display: 'grid', gap: 10,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {[
              ['vender',     '🛒', 'Vender',            'var(--venta)'],
              ['entradas',   '🚚', 'Entró mercancía',   'var(--acero)'],
              ['clientes',   '👥', 'Cobrar',            'var(--ojo)'],
              ['gastos',     '💸', 'Anotar un gasto',   'var(--malo)'],
              ['inventario', '📦', 'Ver el inventario', 'var(--acento)'],
              ['ganancias',  '💰', 'Ver ganancias',     'var(--obra)'],
            ].map(([id, ico, texto, color]) => (
              <button key={id} onClick={() => irA(id)}
                      style={{ padding: '16px 14px', cursor: 'pointer', font: 'inherit',
                               borderRadius: 'var(--radio)', border: '1px solid var(--raya)',
                               background: 'var(--tarjeta)', textAlign: 'left' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{ico}</div>
                <div style={{ fontWeight: 700, color }}>{texto}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Panel;
